import React, { useState, useMemo } from 'react';
import { Bot, Search, Workflow, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { contentApi } from '@/services/contentApi';

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
  folder_path: string;
  tags?: string[];
  published_version?: number;
  draft_version?: number;
}

// Special value for creating a new agent
export const NEW_AGENT_VALUE = '__new__';

interface AgentSelectorProps {
  value: string;
  onChange: (agentId: string) => void;
  agents: ContentItem[];
  disabled?: boolean;
  showNewOption?: boolean;
}

export function AgentSelector({
  value,
  onChange,
  agents,
  disabled = false,
  showNewOption = true,
}: AgentSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter agents by search query
  const filteredAgents = useMemo(() => {
    if (!searchQuery) return agents;
    const query = searchQuery.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.name.toLowerCase().includes(query) ||
        agent.folder_path.toLowerCase().includes(query) ||
        agent.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  }, [agents, searchQuery]);

  // Get selected agent
  const selectedAgent = agents.find((a) => a.id === value);

  // Determine if agent is a graph based on content or tags
  const isGraph = (agent: ContentItem) => {
    return agent.tags?.includes('graph') || agent.name.includes('graph');
  };

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-72">
        <SelectValue placeholder="Select an agent...">
          {value === NEW_AGENT_VALUE ? (
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-green-500" />
              <span className="truncate">New Agent</span>
            </div>
          ) : selectedAgent ? (
            <div className="flex items-center gap-2">
              {isGraph(selectedAgent) ? (
                <Workflow className="h-4 w-4 text-purple-500" />
              ) : (
                <Bot className="h-4 w-4 text-primary" />
              )}
              <span className="truncate">{selectedAgent.name}</span>
            </div>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-80">
        {/* Search input */}
        <div className="flex items-center px-2 pb-2 border-b">
          <Search className="h-4 w-4 text-muted-foreground mr-2" />
          <Input
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        <div className="max-h-64 overflow-y-auto">
          {/* New Agent option */}
          {showNewOption && !searchQuery && (
            <SelectItem value={NEW_AGENT_VALUE} className="py-2 border-b">
              <div className="flex items-center gap-2 w-full">
                <Plus className="h-4 w-4 text-green-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">New Agent</span>
                  <p className="text-xs text-muted-foreground">
                    Create a new agent from scratch
                  </p>
                </div>
              </div>
            </SelectItem>
          )}

          {filteredAgents.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No agents found
            </div>
          ) : (
            filteredAgents.map((agent) => (
              <SelectItem
                key={agent.id}
                value={agent.id}
                className="flex items-start py-2"
              >
                <div className="flex items-center gap-2 w-full">
                  {isGraph(agent) ? (
                    <Workflow className="h-4 w-4 text-purple-500 shrink-0" />
                  ) : (
                    <Bot className="h-4 w-4 text-primary shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{agent.name}</span>
                      {agent.draft_version && agent.draft_version > (agent.published_version || 0) && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          Draft
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {agent.folder_path}
                    </p>
                  </div>
                </div>
              </SelectItem>
            ))
          )}
        </div>
      </SelectContent>
    </Select>
  );
}

export default AgentSelector;
